"use client";

import { useEffect, useRef, useState } from "react";

const PHOTOWALL_DEMO_VIDEO_URL =
  "https://images.guest.gallery/frontpage/cut_demo.webm";

export default function LazyPhotowallVideo() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || shouldLoadVideo) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoadVideo(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "360px 0px",
        threshold: 0.1,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [shouldLoadVideo]);

  useEffect(() => {
    if (!shouldLoadVideo) {
      return;
    }

    void videoRef.current?.play().catch(() => {
      // Autoplay can be blocked in some browser settings; the preview can stay still.
    });
  }, [shouldLoadVideo]);

  return (
    <div
      ref={containerRef}
      className="w-full overflow-hidden rounded bg-transparent"
    >
      <video
        ref={videoRef}
        aria-label="Live photo wall demo"
        className="aspect-video h-auto w-full rounded object-cover"
        autoPlay={shouldLoadVideo}
        controls={false}
        controlsList="nodownload noplaybackrate noremoteplayback"
        disablePictureInPicture
        disableRemotePlayback
        loop
        muted
        playsInline
        preload={shouldLoadVideo ? "metadata" : "none"}
      >
        {shouldLoadVideo ? (
          <source src={PHOTOWALL_DEMO_VIDEO_URL} type="video/webm" />
        ) : null}
      </video>
    </div>
  );
}
